// Dashboard: GET /api/v1/admin/platform/dashboard, plus the usage overview.
//
// The 14-day "inactive" rule is applied by the page on top of the server's
// attention list (see inactiveAttention), because the server's rule is 30 days.

import { apiFetch } from './client'
import type { AccountStatus, AttentionItem, DashboardResponse, TenantRef, UsageOverview } from './types'

const DAY = 24 * 60 * 60 * 1000
const SEVERITY_RANK = { high: 0, medium: 1, low: 2 } as const

export function sortAttention(items: AttentionItem[]): AttentionItem[] {
  // Severity first, then by `at` (oldest first; items with no date last).
  const at = (i: AttentionItem) => i.at ?? '￿'
  return [...items].sort(
    (x, y) => SEVERITY_RANK[x.severity] - SEVERITY_RANK[y.severity] || at(x).localeCompare(at(y)) || x.tenant.name.localeCompare(y.tenant.name),
  )
}

export const INACTIVE_DAYS = 14

/**
 * "No tenant-admin activity in 14+ days", for tenants that are live or in
 * trial (a suspended or canceled tenant being quiet isn't news). A tenant
 * that has never been active counts from its creation date.
 */
export function inactiveAttention(
  rows: Array<{ tenant: TenantRef; status: AccountStatus | null; last_activity_at: string | null; created_at: string }>,
  now: number = Date.now(),
  days: number = INACTIVE_DAYS,
): AttentionItem[] {
  const items: AttentionItem[] = []
  for (const r of rows) {
    if (r.status === 'suspended' || r.status === 'canceled') continue
    const lastIso = r.last_activity_at ?? r.created_at
    const last = new Date(lastIso).getTime()
    if (Number.isNaN(last)) continue
    const idle = Math.floor((now - last) / DAY)
    if (idle < days) continue
    items.push({
      kind: 'inactive',
      severity: 'low',
      tenant: r.tenant,
      message: r.last_activity_at ? `No league-admin activity in ${idle} days` : `No league-admin activity since it was created ${idle} days ago`,
      at: lastIso,
      tab: 'activity',
    })
  }
  return items
}

export function usageOverview(signal?: AbortSignal): Promise<UsageOverview> {
  return apiFetch('/admin/platform/usage/overview', { signal })
}

export function getDashboard(signal?: AbortSignal): Promise<DashboardResponse> {
  return apiFetch('/admin/platform/dashboard', { signal })
}
